import axios from "axios";
import {
  JOB_VISIBILITY_PRIVATE,
  LIST_JOB_ADVERTS_URL,
  VIEW_PENDING_APPLICATIONS_URL,
} from "@/utils/constants/constants.js";

function get_job_profile_collection(primary, fallback) {
  return Array.isArray(primary) ? primary : (Array.isArray(fallback) ? fallback : []);
}
function get_job_profile_field_value(original, key, fallback = "") {
  return Reflect.get(original, key) || fallback;
}
function get_job_profile_vacancies(original) {
  return original.vacancies ?? null;
}
function build_prepopulated_job_fields(original) {
  const fields = {
    edit_trust_type_name: original.entity_type_name,
    edit_trust_type_description: original.entity_type_description,
    edit_trust_type_id: original.id,
    edit_reference_number: get_job_profile_field_value(original, "reference_number"),
    edit_closing_date: get_job_profile_field_value(original, "closing_date"),
    edit_published_date: get_job_profile_field_value(original, "published_date"),
    edit_vacancies: get_job_profile_vacancies(original),
    edit_visibility: get_job_profile_field_value(original, "visibility", JOB_VISIBILITY_PRIVATE),
  };
  return fields;
}
function build_job_profile_meta_date(original, row) {
  return row["Date Created"] || original?.date_created || original?.created_at || "";
}
function get_data_collection_count(value) {
  return value && typeof value === "object" && Array.isArray(value.data) ? value.data.length : null;
}

export default {
  methods: {
    get_trust_type_by_id(entity_type_id) {
      const id = Number(entity_type_id);
      return this.trust_types.find((item) => Number(item.id) === id) || null;
    },
    resolve_job_action_target(row) {
      const entity_type_id = row.__row_id || row.id;
      const original = this.get_trust_type_by_id(entity_type_id);
      const target = { entity_type_id, original, name: original?.entity_type_name || row["Job Profile"] || "" };
      return target;
    },
    set_selected_job_action(target) {
      this.selected_job_action_id = target.original?.id || target.entity_type_id;
      this.selected_job_action_name = target.name;
    },
    select_job_for_actions(row) {
      this.set_selected_job_action(this.resolve_job_action_target(row));
    },
    toggle_job_action_menu(row) {
      this.select_job_for_actions(row);
      const is_open = this.open_job_action_menu_id === this.selected_job_action_id;
      this.open_job_action_menu_id = is_open ? null : this.selected_job_action_id;
    },
    close_job_action_menu() {
      this.open_job_action_menu_id = null;
    },
    get_selected_job_action_row() {
      return this.selected_job_action_id ? this.get_trust_type_by_id(this.selected_job_action_id) : null;
    },
    show_modal(modal_id) {
      const modal_element = document.getElementById(modal_id);
      if (!modal_element || !window.bootstrap?.Modal) return;
      const modal_instance = window.bootstrap.Modal.getInstance(modal_element) || new window.bootstrap.Modal(modal_element);
      modal_instance.show();
    },
    close_then_show_modal(current_modal_id, next_modal_id) {
      this.close_modal(current_modal_id);
      setTimeout(() => this.show_modal(next_modal_id), 180);
    },
    prepopulate_edit_modal(row) {
      const original = this.get_trust_type_by_id(row.__row_id || row.id);
      if (!original) return;
      Object.assign(this, build_prepopulated_job_fields(original));
    },
    open_job_profile_route(entity_type_id, tab = "Job Descriptions") {
      const selected = this.get_trust_type_by_id(entity_type_id);
      sessionStorage.setItem("entity_type_id", entity_type_id);
      sessionStorage.setItem("selected_job_profile_id", entity_type_id);
      sessionStorage.setItem("entity_type_name", selected?.entity_type_name || "Unknown Entity Type");
      localStorage.setItem("active_tab", tab);
      this.$router.push("/view_entity_type");
    },
    view_job_description(row) {
      const target = this.resolve_job_action_target(row);
      this.set_selected_job_action(target);
      this.view_job_entity_type_id = target.entity_type_id;
      this.view_job_profile_meta = this.build_job_profile_meta(target.original, row);
      this.show_modal("view_job_profile_modal");
      this.hydrate_job_profile_stats(target.entity_type_id);
    },
    view_selected_job_description() {
      if (!this.selected_job_action_id) return;
      const original = this.get_selected_job_action_row();
      this.view_job_entity_type_id = this.selected_job_action_id;
      this.view_job_profile_meta = this.build_job_profile_meta(original);
      this.close_job_action_menu();
      this.show_modal("view_job_profile_modal");
      this.hydrate_job_profile_stats(this.selected_job_action_id);
    },
    build_job_profile_meta(original = {}, row = {}) {
      const adverts = get_job_profile_collection(original?.adverts, original?.job_adverts);
      const applicants = get_job_profile_collection(original?.applicants, original?.applications);
      const meta = {
        date_created: build_job_profile_meta_date(original, row),
        department: original?.department || original?.branch || "",
        salary_range: original?.salary_range || "",
        adverts_count: this.get_count_from_record(original, ["adverts_count", "advert_count", "job_adverts_count", "adverts", "job_adverts"]),
        applicants_count: this.get_count_from_record(original, ["applicants_count", "applicant_count", "applications_count", "applications", "applicants"]),
        shortlisted_count: this.get_count_from_record(original, ["shortlisted_count", "shortlist_count", "shortlisted", "shortlisted_applicants"]),
        adverts,
        applicants,
        stats_loading: true,
      };
      return meta;
    },
    get_record_collection_count(value) {
      if (Array.isArray(value)) return value.length;
      return get_data_collection_count(value);
    },
    get_record_numeric_count(value) {
      const count = Number(value);
      return Number.isFinite(count) ? count : null;
    },
    get_record_count_value(record, key) {
      const value = Reflect.get(record, key);
      return this.get_record_collection_count(value) ?? this.get_record_numeric_count(value);
    },
    get_count_from_record(record, keys = []) {
      if (!record || typeof record !== "object") return 0;
      const count = keys.map((key) => this.get_record_count_value(record, key)).find((value) => value !== null);
      return count ?? 0;
    },
    extract_response_list(payload) {
      const candidates = [payload, payload?.data, payload?.results, payload?.list_pending_entities, payload?.data?.list_pending_entities];
      return candidates.find((candidate) => Array.isArray(candidate)) || [];
    },
    build_job_stats_base_meta() {
      const base_meta = { ...this.view_job_profile_meta, stats_loading: true };
      return base_meta;
    },
    build_view_applicants_query(entity_type_id, advert_id = null) {
      const view_applicants_query = {
        entity_type_id: String(entity_type_id),
        entity_type_name: this.selected_job_action_name || "",
        ...(Number.isFinite(advert_id) && advert_id > 0 ? { advert_id: String(advert_id) } : {}),
      };
      return view_applicants_query;
    },
    sync_view_applicants_session(entity_type_id, advert_id = null) {
      sessionStorage.setItem("entity_type_id", String(entity_type_id));
      sessionStorage.setItem("entity_type_name", this.selected_job_action_name || "");
      if (Number.isFinite(advert_id) && advert_id > 0) {
        sessionStorage.setItem("advert_id", String(advert_id));
        return;
      }
      sessionStorage.removeItem("advert_id");
    },
    build_job_stats_requests(safe_entity_type_id, project_id) {
      return [
        axios.get(LIST_JOB_ADVERTS_URL, { headers: this.headers, params: { entity_type_id: safe_entity_type_id } }),
        axios.get(VIEW_PENDING_APPLICATIONS_URL, {
          headers: this.headers,
          params: { ...(Number.isFinite(project_id) && project_id > 0 ? { project_id } : {}), entity_type_id: safe_entity_type_id },
        }),
      ];
    },
    get_settled_response_list(result) {
      return result.status === "fulfilled" ? this.extract_response_list(result.value?.data) : [];
    },
    get_shortlisted_applicants_count(applicants = []) {
      return applicants.filter((applicant) => String(applicant?.status?.status || applicant?.status || "").toLowerCase().includes("shortlisted")).length;
    },
    apply_hydrated_job_profile_meta(base_meta, adverts, applicants) {
      this.view_job_profile_meta = {
        ...base_meta,
        adverts,
        applicants,
        adverts_count: adverts.length,
        applicants_count: applicants.length,
        shortlisted_count: this.get_shortlisted_applicants_count(applicants),
        stats_loading: false,
      };
    },
    async hydrate_job_profile_stats(entity_type_id) {
      const request_id = this.view_job_stats_request_id + 1;
      this.view_job_stats_request_id = request_id;
      const project_id = parseInt(this.project_id, 10);
      const safe_entity_type_id = parseInt(entity_type_id, 10);
      if (!Number.isFinite(safe_entity_type_id) || safe_entity_type_id <= 0) return;
      const base_meta = this.build_job_stats_base_meta();
      this.view_job_profile_meta = base_meta;
      const [adverts_result, applicants_result] = await Promise.allSettled(this.build_job_stats_requests(safe_entity_type_id, project_id));
      if (request_id !== this.view_job_stats_request_id) return;
      this.apply_hydrated_job_profile_meta(base_meta, this.get_settled_response_list(adverts_result), this.get_settled_response_list(applicants_result));
    },
    add_job_advert(row) {
      this.open_job_profile_route(row.__row_id || row.id, "Job Adverts");
    },
    add_selected_job_advert() {
      if (!this.selected_job_action_id) return;
      this.close_job_action_menu();
      this.open_job_profile_route(this.selected_job_action_id, "Job Adverts");
    },
    open_selected_job_advert_modal() {
      if (!this.selected_job_action_id) return;
      const original = this.get_selected_job_action_row();
      this.selected_row = { id: Number(this.selected_job_action_id), entity_type: original?.entity_type_name || this.selected_job_action_name || "" };
      this.create_advert_open_nonce += 1;
      this.close_job_action_menu();
      this.show_modal("create_advert_modal");
    },
    open_drawer_advert_edit_modal(advert) {
      if (!advert?.id) {
        this.show_error("Unable to open advert: missing advert details.");
        return;
      }
      this.selected_advert = { ...advert, entity_type_id: advert.entity_type_id || this.selected_job_action_id || this.view_job_entity_type_id };
      this.edit_advert_open_nonce += 1;
      this.show_modal("edit_advert_modal");
    },
    redirect_drawer_advert_applicants(advert) {
      const entity_type_id = Number(advert?.entity_type_id || this.selected_job_action_id || this.view_job_entity_type_id);
      const advert_id = Number(advert?.id);
      if (!this.has_valid_entity_type_id(entity_type_id)) {
        this.show_error("Unable to open applicants: missing job profile.");
        return;
      }
      this.sync_view_applicants_session(entity_type_id, advert_id);
      this.close_modal("view_job_profile_modal");
      this.$router.push({ path: "/view_applicants", query: this.build_view_applicants_query(entity_type_id, advert_id) });
    },
    has_valid_entity_type_id(entity_type_id) {
      return Number.isFinite(entity_type_id) && entity_type_id > 0;
    },
    redirect_drawer_profile_applicants() {
      const entity_type_id = Number(this.selected_job_action_id || this.view_job_entity_type_id);
      if (!Number.isFinite(entity_type_id) || entity_type_id <= 0) {
        this.show_error("Unable to open applicants: missing job profile.");
        return;
      }
      this.sync_view_applicants_session(entity_type_id);
      this.close_modal("view_job_profile_modal");
      this.$router.push({ path: "/view_applicants", query: this.build_view_applicants_query(entity_type_id) });
    },
    prepopulate_selected_job_edit() {
      const original = this.get_selected_job_action_row();
      if (original) this.prepopulate_edit_modal({ __row_id: original.id });
    },
    open_selected_job_edit_modal() {
      this.prepopulate_selected_job_edit();
      this.close_job_action_menu();
      this.show_modal("edit_trust_type");
    },
    open_drawer_job_edit_modal() {
      this.prepopulate_selected_job_edit();
      this.show_modal("edit_trust_type");
    },
    async on_job_profile_saved(payload = {}) {
      if (payload?.title) this.selected_job_action_name = payload.title;
      this.close_modal("edit_trust_type");
      await this.view_created_trust_type();
      if (this.selected_job_action_id) await this.hydrate_job_profile_stats(this.selected_job_action_id);
    },
    async on_advert_created() {
      this.show_success("Advert created successfully.");
      await this.view_created_trust_type();
      if (this.selected_job_action_id) await this.hydrate_job_profile_stats(this.selected_job_action_id);
    },
    async on_advert_updated() {
      this.show_success("Advert updated successfully.");
      await this.view_created_trust_type();
      if (this.selected_job_action_id) await this.hydrate_job_profile_stats(this.selected_job_action_id);
    },
    on_advert_error(message) {
      this.show_error(message || "Advert action failed.");
    },
  },
};
